import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box, Tab, Button, CircularProgress, FormControl, MenuItem, TextField, Typography,
} from '@mui/material';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PRECIPITATION_NAME } from '../../../constants/graphs';
import ConversationsTab from '../ConversationTab';
import useStyles from '../styles';
import DayMonthBaseForm, { ConfigStateField } from '../utils/DayMonthBaseForm';
import { rainfallConfig } from '../../../requests/api/apiClient';
import { ModalState, EMPTY_MODAL_STATE } from '../chartModal';
import useIsClientOperator from '../../../hooks/useIsClientOperator';
import { YearConfig, RainfallConfigByYear } from './builder';

const DEFAULT_YEAR_CONFIG: YearConfig = {
  rainfallStartMonth: 1,
  rainfallStartDay: 1,
  rainfallEndMonth: 12,
  rainfallEndDay: 31,
};

const FORM_CONFIG = {
  title: 'Configuración Serie Precipitaciones',
  startMonthField: 'rainfallStartMonth' as const,
  startDayField: 'rainfallStartDay' as const,
  endMonthField: 'rainfallEndMonth' as const,
  endDayField: 'rainfallEndDay' as const,
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2023; year <= currentYear + 1; year += 1) {
    years.push(year);
  }
  return years;
};

const RainfallConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { enqueueSnackbar } = useSnackbar();

  // Default to the conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [configsByYear, setConfigsByYear] = useState<RainfallConfigByYear>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const currentYearConfig = useMemo(
    () => configsByYear[selectedYear] || DEFAULT_YEAR_CONFIG,
    [configsByYear, selectedYear],
  );

  useEffect(() => {
    const fetchConfig = async () => {
      if (!sectorId) return;

      setLoading(true);
      try {
        const { data } = await rainfallConfig.get(sectorId);
        if (data?.rainfallConfig?.years) {
          setConfigsByYear(data.rainfallConfig.years);
        }
      } catch (err) {
        enqueueSnackbar('No se pudo cargar la configuración de precipitaciones', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [sectorId, enqueueSnackbar]);

  const handleYearChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedYear(Number(event.target.value));
  }, []);

  const handleConfigChange = useCallback((field: ConfigStateField, value: string | number) => {
    setConfigsByYear((prev) => ({
      ...prev,
      [selectedYear]: {
        ...(prev[selectedYear] || DEFAULT_YEAR_CONFIG),
        [field]: value,
      },
    }));
  }, [selectedYear]);

  const handleSave = useCallback(async () => {
    if (Object.keys(configsByYear).length === 0) {
      enqueueSnackbar('No hay configuraciones para guardar', {
        variant: 'warning',
      });
      return;
    }

    setSaving(true);
    try {
      await rainfallConfig.update(sectorId, configsByYear);
      enqueueSnackbar('Configuración guardada correctamente', {
        variant: 'success',
      });
      onConfigSaved?.();
    } catch (err) {
      enqueueSnackbar('Error al guardar la configuración', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [sectorId, configsByYear, enqueueSnackbar, onConfigSaved]);

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  }, []);

  const yearOptions = useMemo(() => generateYearOptions(), []);

  return (
    <TabContext value={tabValue}>
      <Box sx={{ borderBottom: 0 }}>
        <TabList onChange={handleTabChange} aria-label="sector tabs">
          {!isClientOperator && (
            <Tab label="Configuración" value="1" className={classes.tabLabel} />
          )}
          <Tab label="Alertas" value="2" className={classes.tabLabel} />
          <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
        </TabList>
      </Box>
      {!isClientOperator && (
        <TabPanel value="1" className={classes.configTabPanel}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  background: '#fff',
                  borderRadius: 2,
                  p: 4,
                  boxShadow: 1,
                  mb: 2,
                  maxWidth: '100%',
                }}
              >
                <Typography variant="h5" mb={3} fontWeight="bold">
                  Seleccionar Año
                </Typography>
                <FormControl fullWidth sx={{ maxWidth: 300 }}>
                  <TextField
                    select
                    label="Año"
                    value={selectedYear}
                    onChange={handleYearChange}
                    variant="standard"
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </Box>

              <DayMonthBaseForm
                {...FORM_CONFIG}
                config={currentYearConfig}
                onConfigChange={handleConfigChange}
                errors={{}}
              />

              <Box mt={3} display="flex" justifyContent="flex-end">
                {saving ? <CircularProgress /> : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    Guardar
                  </Button>
                )}
              </Box>
            </>
          )}
        </TabPanel>
      )}
      <TabPanel value="2" className={classes.configTabPanel}>
        In progress
      </TabPanel>
      <TabPanel value="3" className={classes.configTabPanel}>
        <ConversationsTab
          setModalState={setModalState}
          sectorId={sectorId}
          chartName={PRECIPITATION_NAME}
          refreshCounter={refreshCounter}
        />
      </TabPanel>
    </TabContext>
  );
};

export default RainfallConfigContent;
